import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { GetSavannaCurriculumDocument } from '@sandtech-hub-monorepo/ehub-client';

import { CourseAccordion } from './CourseAccordion';
import { ModuleRow } from './ModuleRow';
import {
  lockedBeforeFetch,
  SavannaCourseLocation,
  savannaCourseToCourse,
} from '../../data/savannaCurriculum';
import { CurriculumError } from './CurriculumError';
import { ModuleListSkeleton } from './ModuleListSkeleton';

interface Props {
  location: SavannaCourseLocation;
  /** 1-based position in the rendered list, shown in the accordion badge. */
  index: number;
  paidUnlocked: boolean;
  onSubscribe: () => void;
}

/**
 * Courses 2..N of the informative tree.
 *
 * Fetched eagerly on first render, same as course 1 and same as
 * `InteractiveCourseAccordion` - product wants the full paywall split and
 * module list available for every course up front rather than deferred to a
 * click. The header still starts closed; only its content is already in hand
 * when the learner opens it.
 */
export const InformativeCourseAccordion = ({
  location,
  index,
  paidUnlocked,
  onSubscribe,
}: Props) => {
  const { data, loading, error } = useQuery(GetSavannaCurriculumDocument, {
    variables: { id: location.curriculumId },
    fetchPolicy: 'cache-first',
  });

  const flow = data?.savannaCurriculum?.flow;

  const course = useMemo(() => {
    const mapped = savannaCourseToCourse(location, index, flow, false);

    // Until the flow arrives there are no blocks to read `locked` from, so
    // the mapping says "not locked" - stand in with the product rule for the
    // brief window this fetch is in flight.
    return flow ? mapped : { ...mapped, locked: lockedBeforeFetch(index) };
  }, [location, index, flow]);

  return (
    <CourseAccordion
      course={course}
      // The default "has modules" test would keep the header disabled for as
      // long as this course's own fetch is in flight - it should open right
      // away and show the loading skeleton, same as course 1 does.
      canOpen
      paidUnlocked={paidUnlocked}
      onSubscribe={onSubscribe}
    >
      {loading && <ModuleListSkeleton />}
      {!loading && error && <CurriculumError subject="this course" />}
      {!loading && !error && (
        <div className="flex flex-col divide-y divide-border-secondary">
          {course.modules.map((module, moduleIndex) => (
            <ModuleRow
              key={module.id}
              index={moduleIndex + 1}
              module={module}
              paidUnlocked={paidUnlocked}
            />
          ))}
        </div>
      )}
    </CourseAccordion>
  );
};
